import {
  CHART_NOTES_GENERAL_TYPE,
  CHART_NOTES_LIMIT,
  CHART_NOTES_VISIT_TYPE
} from "constants/chart-notes";
import { formatChartNote } from "utils/chart-notes.util";
import { formatValidQueryParamUrl } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  ChartNote,
  ChartNoteBulkPinInput,
  ChartNoteCountsParams,
  ChartNoteCountsResponse,
  ChartNoteFilters,
  ChartNoteHistoryResponse,
  ChartNoteInput,
  ChartNoteMergeOption,
  ChartNotesResponse
} from "./types/chart-notes.type";
import type { PromptOutputFormat } from "./types/prompts.type";

// `dialstack_call` is a SerializerMethodField on the backend, so it is requested
// flat (RestQL cannot sub-project a method field). Backend must deploy first —
// an unknown read key raises FieldNotFound.
const CHART_NOTE_HISTORY_QUERY =
  "{id,text,created_at,updated_at,created_by,updated_by,crud_events{id,event_type,datetime,changed_fields,user{id,full_name}}}";

const CHART_NOTE_QUERY =
  "{id,type,text,created_by,updated_by,created_at,updated_at,dialstack_call,clinic{id,name,display_name,logo,practice{id,name,logo}}}";

const CHART_NOTE_MERGE_OPTION_QUERY =
  "{id,type,status,display_moment,appointment{id,type{id,name},staff_member{id,full_name}},clinic{id,timezone}}";

const CHART_NOTE_MERGE_OPTIONS_LIMIT = 50;

export const chartNotesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getChartNotes: builder.query<ChartNotesResponse, ChartNoteFilters>({
      query: ({
        patient,
        offset = 0,
        limit = CHART_NOTES_LIMIT,
        query = "{}",
        // Day of the visit, then the order the notes were written (see backend ChartNoteViewSet.ordering):
        // the appointment note created on arrival sits above the note the front desk typed minutes earlier.
        ordering = "-pinned,-display_day,-written_moment,-id",
        ...filters
      }) =>
        `/chart-notes/?patient=${patient}&limit=${limit}&offset=${offset}&${formatValidQueryParamUrl(
          filters
        )}&ordering=${ordering}&query=${query}`,
      providesTags: (result = []) => [
        ...result.map((chartNote) => ({
          type: "ChartNotes" as const,
          id: chartNote.id
        })),
        { type: "ChartNotes" as const, id: "LIST" }
      ],
      transformResponse: (response: ChartNotesResponse) =>
        response.map(formatChartNote)
    }),
    getChartNote: builder.query<ChartNote, { noteId: number; query?: string }>({
      query: ({ noteId, query = CHART_NOTE_QUERY }) =>
        `/chart-notes/${noteId}/?query=${query}`,
      providesTags: (chartNote) => [
        {
          type: "ChartNotes" as const,
          id: chartNote?.id
        }
      ],
      transformResponse: formatChartNote
    }),
    // SN-19901: the chart notes a merged scribe's new note can go into. A slim read with no `text`,
    // so no `formatChartNote` (it derives the preview from `text`). GENERAL and APPOINTMENT notes
    // only: PHONE notes are auto-filed call logs and BILLING notes never hold a scribe note. A note
    // a selected scribe is linked to is always included, whatever its type or age (`linkedIds`).
    getChartNoteMergeOptions: builder.query<
      ChartNoteMergeOption[],
      { patient: number; linkedIds: number[] }
    >({
      async queryFn({ patient, linkedIds }, _api, _extra, baseQuery) {
        const list = await baseQuery(
          `/chart-notes/?patient=${patient}&type=${CHART_NOTES_GENERAL_TYPE},${CHART_NOTES_VISIT_TYPE}&limit=${CHART_NOTE_MERGE_OPTIONS_LIMIT}&ordering=-display_day,-written_moment,-id&query=${CHART_NOTE_MERGE_OPTION_QUERY}`
        );
        if (list.error) return { error: list.error };
        const notes = list.data as ChartNoteMergeOption[];
        const listed = new Set(notes.map((note) => note.id));
        const missing = linkedIds.filter((id) => !listed.has(id));
        const extras = await Promise.all(
          missing.map((id) =>
            baseQuery(
              `/chart-notes/${id}/?query=${CHART_NOTE_MERGE_OPTION_QUERY}`
            )
          )
        );
        // A linked note that has since been deleted is a 404: it just is not offered.
        const found = extras
          .filter((result) => !result.error)
          .map((result) => result.data as ChartNoteMergeOption);
        return { data: [...notes, ...found] };
      },
      providesTags: [{ type: "ChartNotes" as const, id: "LIST" }],
      keepUnusedDataFor: 0,
      // The merge dialog shows its own error and Retry; no second, global toast.
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    // ENG-1833: the note's audit trail, for the History dialog only. Fetched lazily per note when
    // the dialog opens — never from a list, because a heavily edited note carries every earlier
    // version of its text (megabytes on an autosave-heavy note). No `formatChartNote`: the dialog
    // unwraps legacy text per version itself.
    getChartNoteHistory: builder.query<
      ChartNoteHistoryResponse,
      { noteId: number }
    >({
      query: ({ noteId }) =>
        `/chart-notes/${noteId}/?query=${CHART_NOTE_HISTORY_QUERY}`,
      providesTags: (_result, _error, { noteId }) => [
        { type: "ChartNotes" as const, id: noteId }
      ],
      keepUnusedDataFor: 0
    }),
    getChartNoteCounts: builder.query<
      ChartNoteCountsResponse,
      ChartNoteCountsParams
    >({
      query: ({ patient }) => `/chart-notes/count/?patient=${patient}`,
      providesTags: (_result, _err, arg) => [
        { type: "ChartNoteCounts" as const, id: arg.patient }
      ]
    }),
    addChartNote: builder.mutation<ChartNote, Partial<ChartNoteInput>>({
      query: (noteData) => ({
        url: "/chart-notes/",
        method: "POST",
        body: noteData
      }),
      transformResponse: formatChartNote,
      invalidatesTags: (result, _error, arg) => {
        const tags: Array<
          | "ChartNotes"
          | "Appointments"
          | "ChartNoteCounts"
          | { type: "ChartNoteCounts"; id: number }
        > = ["ChartNotes", "Appointments"];

        if (typeof arg?.patient === "number") {
          tags.push({ type: "ChartNoteCounts", id: arg.patient });
          return tags;
        }

        if (result) {
          const pid =
            typeof result.patient === "number"
              ? result.patient
              : result.patient?.id;
          if (typeof pid === "number") {
            tags.push({ type: "ChartNoteCounts", id: pid });
            return tags;
          }
        }

        tags.push("ChartNoteCounts");
        return tags;
      }
    }),
    updateChartNote: builder.mutation<ChartNote, Partial<ChartNoteInput>>({
      query: ({ id, ...noteData }) => ({
        url: `/chart-notes/${id}/`,
        method: "PATCH",
        body: noteData
      }),
      transformResponse: formatChartNote,
      invalidatesTags: (chartNote) => [
        {
          type: "ChartNotes" as const,
          id: chartNote?.id
        }
      ]
    }),
    bulkPinChartNote: builder.mutation<ChartNote, ChartNoteBulkPinInput>({
      query: (data) => ({
        url: `/chart-notes/bulk-pin/`,
        method: "POST",
        body: {
          pin: data?.pin?.join(","),
          unpin: data?.unpin?.join(",")
        }
      }),
      transformResponse: formatChartNote,
      invalidatesTags: ["ChartNotes"]
    }),
    deleteChartNote: builder.mutation<string, number>({
      query: (id) => ({
        url: `/chart-notes/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["ChartNotes", "ChartNoteCounts"]
    }),

    // ─── @mentions (TEAM_MENTIONS_FEATURE; candidates live in services/mentions) ─
    // `team_ids` / `notified_team_ids` (TEAMS_FEATURE): every member of each team is notified per the
    // team's alert mode; sent only with that flag on.
    notifyChartNoteMentions: builder.mutation<
      { notified_user_ids: number[]; notified_team_ids?: number[] },
      {
        id: number;
        user_ids: number[];
        create_task?: boolean;
        team_ids?: number[];
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/chart-notes/${id}/notify-mentions/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ChartNotes" as const, id }
      ]
    }),
    retractChartNoteMentions: builder.mutation<
      { retracted_user_ids: number[] },
      { id: number; user_ids: number[]; team_ids?: number[] }
    >({
      query: ({ id, ...body }) => ({
        url: `/chart-notes/${id}/retract-mentions/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ChartNotes" as const, id }
      ]
    }),
    markChartNoteMentionsSeen: builder.mutation<{ updated: number }, number>({
      query: (id) => ({
        url: `/chart-notes/${id}/mark-mentions-seen/`,
        method: "POST"
      }),
      // The bell notification is marked read server-side too.
      invalidatesTags: (result, _error, id) =>
        result?.updated
          ? [
              { type: "ChartNotes" as const, id },
              "Notifications",
              "NotificationUnreadCounts"
            ]
          : []
    }),
    // The mentioned person's "Make it a task": creates (or returns) their follow-up task.
    createChartNoteMentionTask: builder.mutation<{ task_id: number }, number>({
      query: (id) => ({
        url: `/chart-notes/${id}/mention-task/`,
        method: "POST"
      }),
      // The bell row then reads the new task id and says "Open task".
      invalidatesTags: ["StaffTasks", "Notifications"]
    }),

    autoCompleteChartNoteAskAi: builder.mutation<
      string,
      {
        id: number;
        clinic_id?: number;
        text?: string;
        user_prompt?: string;
        as_soap_note?: boolean;
        format?: PromptOutputFormat;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/chart-notes/${id}/complete`,
        method: "POST",
        body
      })
    })
  })
});

export const {
  useGetChartNotesQuery,
  useGetChartNoteQuery,
  useGetChartNoteMergeOptionsQuery,
  useLazyGetChartNoteHistoryQuery,
  useGetChartNoteCountsQuery,
  useAddChartNoteMutation,
  useUpdateChartNoteMutation,
  useBulkPinChartNoteMutation,
  useDeleteChartNoteMutation,
  useAutoCompleteChartNoteAskAiMutation,
  useNotifyChartNoteMentionsMutation,
  useRetractChartNoteMentionsMutation,
  useMarkChartNoteMentionsSeenMutation,
  useCreateChartNoteMentionTaskMutation
} = chartNotesApi;
